/**
 * ============================================================================
 * BUDGET LIMITS v3.0 - CARD LAYOUT (wie Transactions)
 * ============================================================================
 *
 * ✅ v3.0: CARD LAYOUT
 *          - Outer card wrapper (white bg, border, rounded corners)
 *          - Card header: title left + add button right
 *          - Content: limit grid inside card
 * ✅ v2.2: Button Strategy Alignment
 * ✅ v2.1: Fix Kategorie-Filter
 * ✅ v2.0: Add Limit Button + Planivio Styling
 *
 * @version 3.0.0
 */

const TEAL = '#4A90A4';
const GREEN = '#6BA893';
const BORDER = '#B8D4DC';
const BORDER_LIGHT = '#D4E8EC';
const BG_LIGHT = '#E8F4F8';

// ── Card layout styles (same as BudgetTransactions) ────────────────────────
const card = {
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${BORDER}`,
  boxShadow: '0 2px 8px rgba(74,144,164,0.1)',
  overflow: 'hidden'
};
const cardHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.45rem 1.25rem',
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  background: BG_LIGHT
};
const cardTitle = {
  margin: 0,
  fontSize: '1.1rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#1F2937'
};
// L1 Primary (52px, 14px radius) — einzige Hauptaktion pro Tab
const addBtn = {
  padding: '0 28px',
  minHeight: '52px',
  background: `linear-gradient(135deg, ${TEAL} 0%, ${GREEN} 100%)`,
  color: 'white',
  border: 'none',
  borderRadius: '14px',
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s',
  boxShadow: '0 4px 16px rgba(74,144,164,0.25)',
  lineHeight: 1,
  boxSizing: 'border-box'
};
const cardContent = {
  padding: '1.25rem 1.5rem'
};

function BudgetLimits({ limits, categories, transactions, onDelete, onAdd, onEdit }) {

  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);

  const getCategoryInfo = (name) => {
    const cat = categories.find(c => c.name === name);
    return cat || { name, icon: '💰', color: TEAL };
  };

  const calculateSpending = (limit) => {
    return transactions
      .filter(t => t.category === limit.category && t.type === 'expense')
      .filter(t => {
        const txDate = new Date(t.date);
        return txDate.getMonth() + 1 === limit.month &&
               txDate.getFullYear() === limit.year;
      })
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0);
  };

  const categoriesWithoutLimit = categories.filter(cat =>
    !limits.find(l => l.category === cat.name)
  );

  return (
    <div style={card}>

      {/* Card Header */}
      <div style={cardHeader}>
        <h2 style={cardTitle}><span>📈</span> Budget-Limits</h2>
      </div>

      {/* Card Content */}
      <div style={cardContent}>
        {limits.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '2rem',
            color: '#6B7280'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📈</div>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#1F2937' }}>
              Noch keine Budget-Limits gesetzt
            </p>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Setze monatliche Limits für deine Ausgaben-Kategorien
            </p>
            {onAdd && (
              <button style={{ ...addBtn, marginTop: '1rem' }} onClick={onAdd}>
                + Erstes Limit erstellen
              </button>
            )}
            {categoriesWithoutLimit.length > 0 && (
              <div style={{
                marginTop: '1.25rem',
                paddingTop: '1.25rem',
                borderTop: `1px solid ${BORDER}`,
                width: '100%'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
                  💡 {categoriesWithoutLimit.length} Kategorien ohne Limit:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                  {categoriesWithoutLimit.slice(0, 5).map(cat => (
                    <span key={cat.id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 10px', background: BG_LIGHT, borderRadius: '20px',
                      fontSize: '0.8rem', color: '#374151'
                    }}>
                      {cat.icon || '📁'} {cat.name}
                    </span>
                  ))}
                  {categoriesWithoutLimit.length > 5 && (
                    <span style={{
                      padding: '3px 10px', background: 'rgba(74,144,164,0.1)',
                      borderRadius: '20px', fontSize: '0.8rem', color: TEAL
                    }}>
                      +{categoriesWithoutLimit.length - 5} weitere
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '0.75rem'
          }}>
            {limits.map(limit => {
              const cat = getCategoryInfo(limit.category);
              const spent = calculateSpending(limit);
              const limitAmount = parseFloat(limit.limit_amount || limit.amount || 0);
              const pct = limitAmount > 0 ? (spent / limitAmount) * 100 : 0;
              const isOver = pct > 100;
              const isWarn = pct > 80;

              return (
                <div key={limit.id} className="budget-limit-card">
                  <div className="budget-limit-header">
                    <div>
                      <h3 className="budget-limit-title">
                        {cat.icon || '📁'} {cat.name}
                      </h3>
                      <div className="budget-limit-period">
                        {limit.month}/{limit.year}
                        {cat.is_fixed !== undefined && (
                          <span className={`limit-type-tag ${cat.is_fixed ? 'fixed' : 'variable'}`}>
                            {cat.is_fixed ? 'Fix' : 'Var'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="budget-limit-actions">
                      {onEdit && (
                        <button onClick={() => onEdit(limit)} className="budget-btn-icon budget-btn-edit" title="Bearbeiten">✏️</button>
                      )}
                      <button onClick={() => onDelete(limit.id)} className="budget-btn-icon budget-btn-danger" title="Löschen">🗑️</button>
                    </div>
                  </div>

                  <div className="budget-limit-progress">
                    <div className="budget-limit-amounts">
                      <span className={isOver ? 'over' : ''}>{formatCurrency(spent)}</span>
                      <span>{formatCurrency(limitAmount)}</span>
                    </div>
                    <div className="budget-progress-bar">
                      <div
                        className={`budget-progress-fill ${isOver ? 'over' : isWarn ? 'warn' : 'ok'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <div className={`budget-limit-percentage ${isOver ? 'over' : isWarn ? 'warn' : 'ok'}`}>
                      {pct.toFixed(0)}%
                    </div>
                  </div>

                  <div className="budget-limit-remaining">
                    {isOver ? (
                      <span className="over">🚨 {formatCurrency(spent - limitAmount)} über Budget</span>
                    ) : (
                      <span className="ok">✓ {formatCurrency(limitAmount - spent)} übrig</span>
                    )}
                  </div>

                  {isOver && (
                    <div className="budget-limit-alert">🚨 Budget überschritten!</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Styles */}
      <style>{`
        .budget-limit-card {
          background: white;
          border-radius: 10px;
          padding: 0.5rem 0.75rem;
          border: 1px solid ${BORDER};
          transition: all 0.2s;
        }
        .budget-limit-card:hover {
          border-color: ${TEAL};
          box-shadow: 0 2px 8px rgba(74,144,164,0.1);
        }
        .budget-limit-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        .budget-limit-title {
          margin: 0;
          font-size: 0.82rem;
          font-weight: 600;
          color: #1F2937;
        }
        .budget-limit-period {
          font-size: 0.7rem;
          color: #6B7280;
          margin-top: 2px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .limit-type-tag {
          font-size: 0.62rem;
          padding: 1px 5px;
          border-radius: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .limit-type-tag.fixed { background: rgba(74,144,164,0.1); color: ${TEAL}; }
        .limit-type-tag.variable { background: rgba(107,168,147,0.1); color: ${GREEN}; }
        .budget-limit-actions { display: flex; gap: 5px; }
        .budget-btn-icon {
          width: 32px; height: 32px; padding: 0;
          background: transparent;
          border: 1px solid ${BORDER};
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          position: relative;
          line-height: 1;
          box-sizing: border-box;
        }
        .budget-btn-icon::before { content: ''; position: absolute; inset: -6px; }
        .budget-btn-icon:hover { background: ${BG_LIGHT}; }
        .budget-btn-edit:hover { background: #EFF6FF; border-color: #93C5FD; }
        .budget-btn-danger:hover { background: #FEF2F2; border-color: #FECACA; }
        .budget-limit-progress { display: flex; flex-direction: column; gap: 5px; }
        .budget-limit-amounts {
          display: flex;
          justify-content: space-between;
          font-size: 0.82rem;
          color: #6B7280;
        }
        .budget-limit-amounts .over { color: #EF4444; font-weight: 600; }
        .budget-progress-bar {
          height: 8px;
          background: #E5E7EB;
          border-radius: 4px;
          overflow: hidden;
        }
        .budget-progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        .budget-progress-fill.ok { background: linear-gradient(90deg, #22C55E, #10B981); }
        .budget-progress-fill.warn { background: linear-gradient(90deg, #F59E0B, #FBBF24); }
        .budget-progress-fill.over { background: linear-gradient(90deg, #EF4444, #F87171); }
        .budget-limit-percentage { font-size: 0.85rem; font-weight: 700; text-align: right; }
        .budget-limit-percentage.ok { color: #22C55E; }
        .budget-limit-percentage.warn { color: #F59E0B; }
        .budget-limit-percentage.over { color: #EF4444; }
        .budget-limit-remaining {
          margin-top: 0.625rem;
          padding-top: 0.625rem;
          border-top: 1px solid ${BORDER_LIGHT};
          font-size: 0.82rem;
        }
        .budget-limit-remaining .ok { color: #22C55E; }
        .budget-limit-remaining .over { color: #EF4444; }
        .budget-limit-alert {
          margin-top: 0.5rem;
          padding: 6px 10px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 8px;
          font-size: 0.82rem;
          color: #EF4444;
          text-align: center;
        }
        /* Dark Mode */
        .dark .budget-limit-card { background: #1F2937; border-color: #374151; }
        .dark .budget-limit-title { color: #F9FAFB; }
        .dark .budget-limit-remaining { border-top-color: #374151; }
      `}</style>

    </div>
  );
}

export default BudgetLimits;
